import React, { forwardRef } from 'react';
import { Button } from '@smarterservices/smarter-ui';
import PropTypes from 'prop-types';

/**
 * TriggerButton Component
 * 
 * A wrapper around the Button component that properly forwards refs.
 * This is necessary when using Button as a trigger for components like
 * Drilldown, Popover, or other UI library components that need to attach
 * refs to their trigger elements.
 * 
 * **Why this component exists:**
 * 
 * Some UI library components (like Drilldown, Popover, Menu) need to pass
 * refs to their trigger elements for positioning and interaction handling.
 * However, the Button component from @smarterservices/smarter-ui doesn't
 * properly forward refs, causing React warnings:
 * 
 * "Warning: Function components cannot be given refs. Attempts to access 
 * this ref will fail. Did you mean to use React.forwardRef()?"
 * 
 * This component solves that by wrapping the Button in a span element
 * and forwarding the ref to the span, which can properly receive refs
 * as a native DOM element.
 * 
 * @component
 * @example
 * // Using with Drilldown
 * <Drilldown
 *   trigger={
 *     <TriggerButton size="small" screenReaderLabel="Options">
 *       <IconMoreLine />
 *     </TriggerButton>
 *   }
 * >
 *   <Drilldown.Page>...</Drilldown.Page>
 * </Drilldown>
 * 
 * @example
 * // Using with Popover
 * <Popover
 *   renderTrigger={
 *     <TriggerButton>Click me</TriggerButton>
 *   }
 * >
 *   <Popover.Content>...</Popover.Content>
 * </Popover>
 */
const TriggerButton = forwardRef((props, ref) => (
    <span ref={ref}>
        <Button {...props} />
    </span>
));

TriggerButton.displayName = 'TriggerButton';

TriggerButton.propTypes = {
    /** All props are passed through to the underlying Button component */
    ...Button.propTypes,
};

export default TriggerButton;
